import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { AppContext } from "../../contexts/AppContext";
import { getDataSets } from "../../services/data";
import styled from "styled-components";
import { Icon } from "@iconify/react";
import {
  getLocalDateFromString,
  shortDateTimeFormatter,
} from "../../utils/utils";
import { Button, FormSelector } from "../../components/StyledElements";
import DataSetsTable from "../../components/Tables/DataSetsTable";
import swal from "sweetalert2";
import QueryDialog from "../../components/Dialogs/QueryDialog";


const SmallButton = styled.div`
  height: 30px;
  width: 24px;
  background-color: #f3f4f6; 
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a0a6b1;
  cursor: pointer;

  &.selected {
    background-color: var(--button-selected-bg); 
  }
  &.selected > * {
    color: var(--button-bg);

  }
`

const VerticalSeparator = styled.div`
  width: 1px;
  height: 30px;
  background-color: #eceff4;
  margin-left: 20px;
  margin-right: 20px;
`

const DataSetsGridWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;

`

const DataSetCardWrapper = styled.div`
    position: relative;
    border: 1px solid var(--content-item-border-color);
    color: var(--main-font-color);
    width: 300px;
    height: 420px;
    max-height: 485px;
    background-color: var(--card-bg);
    border-radius: 5px;
    box-shadow: var(--content-item-box-shadow);
    padding: 20px 20px 0 20px;
`

const DataSetTitle = styled.div`
    font-weight: bold;
    font-size: 20px;
    margin-top: 20px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    cursor: pointer;
`

const DataSetSubTitle = styled.div`
    font-weight: 500;
    font-size: 16px;
    margin-top: 5px;
    color: var(--main-font-color);
    text-overflow: ellipsis;
    font-style: italic;
    white-space: nowrap;
    overflow: hidden;
    cursor: pointer;
`

const DataSetDescription = styled.div`
    font-weight: normal;
    font-size: 16px;
    margin-top: 10px;
    font-style: italic;
    overflow: auto;
    max-height: 140px;
    height: 140px;
    margin-bottom: 10px;
`

const DataSetInfo = styled.div`
    font-size: 15px;
    margin-bottom: 10px;
    margin-top: 10px;
    display: flex;
    flex-direction: row;
    align-items: center;

    & > div {
      display: flex;
      flex-direction: row;
      align-items: center;
    }
}
`
const DataSetData = styled.div`
    position: relative;
    width: 300px;
    margin-left: -20px;
    font-size: 14px;
    color: #a0a6b1;
    background-color: var(--card-inset-bg);
    padding: 10px 20px;
    border-top: 1px solid var(--main-bg);
    border-bottom: 1px solid var(--main-bg);
`;

const DataSetOpenLink = styled.a`
    font-size: 15px;
    color: var(--sidebar-active-item-color);
    text-decoration: none;
    display: flex;
    flex-direction: row;
    align-items: center;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
`;

const SearchBar = styled.div`
  width: 100%;
  border-radius: 5px;
  border: 1px solid var(--content-item-border-color);  
  background-color: var(--card-bg);
  padding: 10px 20px;
  box-shadow: var(--content-item-box-shadow);
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
`

const SearchBox = styled.div`
  width: 300px;
  height: 36px;
  border-radius: 5px;
  border: 1px solid #d2d6dd;
  background-color: #f8fafb; 
  display: flex;
  align-items: center;
  padding-left: 10px;

  & * {
    color: #717886;
  }

  & input {
    height: 100%;
    border: none;
    outline: none !important;
    background: transparent;
    width: 250px;
  }

  & input:focus {
    outline:none !important;
    outline-width: 0 !important;
    box-shadow: none;
    -moz-box-shadow: none;
    -webkit-box-shadow: none;
  }
}
`

const DataSetToolBarWrapper = styled.div`
  width: 300px;
  margin-left: -20px;
  height: 50px;
  margin-top: 0px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-evenly;
  flex-wrap: nowrap;
  padding: 0;
`

const DataSetToolBarButton = styled.div`
  height: 100%;
  width: 100%;
  padding-top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-right: 1px solid var(--main-bg);
  cursor: pointer;

  & * {
    font-size: 20px;
 }

`;

export const DataSets = ({ show = false }) => {
  const activeUser = useSelector((state) => state.users.activeUser);
  const { selectedClient } = useContext(AppContext);
  const [showQueryDialog, setShowQueryDialog] = useState(false);
  const [queryDataSet, setQueryDataSet] = useState(null);
  const [dataSets, setDataSets] = useState([]);
  const [filteredDataSets, setFilteredDataSets] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("newest-first")
  const [display, setDisplay] = useState("grid");
  const navigate = useNavigate();

  const sortByOptions = [
    { label: "Newest First", value: "newest-first" },
    { label: "Open Date First", value: "open-date-first" },
    { label: "Open Date Last", value: "open-date-last" },
    { label: "Start Date First", value: "start-date-first" },
    { label: "Start Date Last", value: "start-date-last" },
  ]

  useEffect(() => {
    console.log("!!! FETCHING data SETS");
    const fetchDataSets = async () => {
      try {
        const results = await getDataSets(selectedClient.id);
        if (results.success) {
          console.log("!!! FETCHED data SETS: ", results.data_sets);
          setDataSets(results.data_sets);
          setFilteredDataSets(results.data_sets);
        } else {
          swal.fire({
            title: "Error fetching data sets",
            text: results.message || "An error occurred while fetching live events. Please try again later.",
            icon: "error",
            confirmButtonText: "OK"
          });
        }
      } catch (error) {
        console.error(error);
      }
    };

    console.log("!!! SELECTED CLIENT FROM users: ", selectedClient);
    if (selectedClient) {
      fetchDataSets();
    }
    // eslint-disable-next-line
  }, [selectedClient]);


  useEffect(() => {
    if (!searchTerm) {
      setFilteredDataSets(dataSets);
      return;
    }

    setFilteredDataSets(dataSets.filter((dataSet) => {
      console.log(dataSet.title, dataSet.description)
      return `${dataSet.name} ${dataSet.description} ${dataSet.chroma_collection_name}`.toLowerCase().includes(searchTerm.toLowerCase())
    }))

    // eslint-disable-next-line
  }, [searchTerm])

  const DataSetToolBar = ({ dataSet }) => {
    return (
      <DataSetToolBarWrapper>
        {/* Toolbar buttons can be added here */}
        <DataSetToolBarButton onClick={() => {
        }}><Icon icon="mdi:add" /></DataSetToolBarButton>
        <DataSetToolBarButton
          onClick={() => {
          }}
        ><Icon icon="mdi:edit" /></DataSetToolBarButton>
        <DataSetToolBarButton
          title="Query the data set with natural language and get insights, summaries, and more."
          onClick={() => {
            setQueryDataSet(dataSet);
            setShowQueryDialog(true);
          }}
        ><Icon icon="mdi:question-mark" /></DataSetToolBarButton>

        <DataSetToolBarButton><Icon icon="mdi:trash-can" /></DataSetToolBarButton>
      </DataSetToolBarWrapper>
    )
  }

  const getDataSetStatus = (dataSet) => {
    const now = new Date();
    const startDate = getLocalDateFromString(dataSet.start_date);
    const endDate = getLocalDateFromString(dataSet.completed_at);

    if ((dataSet.status === "scheduled" || (startDate && startDate > now)) || (dataSet.status === "initialized" && dataSet.event_type.includes("recorded", "scheduled"))) {
      return "scheduled";
    }

    if (dataSet.status === "ended" || (endDate && endDate < now)) {
      return "ended";
    }

    return dataSet.status
  }

  const DataSetCard = ({ dataSet }) => {
    console.log("!!! RENDERING data SET CARD: ", dataSet);
    const dataSetStatus = getDataSetStatus(dataSet);
    return (
      <DataSetCardWrapper key={dataSet.id}>
        <div style={{ height: "240px", minHeight: "240px" }}>
          <DataSetTitle
            title={dataSet.name}
          >{dataSet.name}</DataSetTitle>
          <DataSetSubTitle>{dataSet.chroma_collection_name}</DataSetSubTitle>
          <DataSetDescription>{dataSet.description ?? "No description"}</DataSetDescription>
        </div>
        {dataSetStatus === "ended" &&
          (
            <>
              <DataSetInfo>
                <Icon icon="mdi:calendar-blank" />
                <div style={{ marginLeft: "10px" }}>Started: {dataSet?.start_date ? shortDateTimeFormatter.format(getLocalDateFromString(dataSet.start_date)) : "-"}</div>
              </DataSetInfo>
              <DataSetInfo>
                <Icon icon="mdi:clock" />
                <div style={{ marginLeft: "10px" }}>Ended: {dataSet?.completed_at ? shortDateTimeFormatter.format(getLocalDateFromString(dataSet.completed_at)) : "-"}</div>
              </DataSetInfo>
              <DataSetInfo>
                <Icon icon="mdi:people" />
                <div style={{ marginLeft: "10px" }}>{dataSet.participant_count} users</div>
              </DataSetInfo>
            </>
          )
        }

        {dataSetStatus === "scheduled" &&
          (
            <>
              <DataSetInfo>
                <Icon icon="mdi:calendar" color={"var(--sidebar-active-item-color)"} style={{ fontWeight: "500" }} />
                <div style={{ marginLeft: "10px", fontWeight: "500" }}>Opens: {dataSet.open_date ? shortDateTimeFormatter.format(getLocalDateFromString(dataSet.open_date)) : "-"}</div>
              </DataSetInfo>
              <DataSetInfo>
                <Icon icon="mdi:clock" />
                <div style={{ marginLeft: "10px" }}>Starts: {dataSet.start_date ? shortDateTimeFormatter.format(getLocalDateFromString(dataSet.start_date)) : "-"}</div>
              </DataSetInfo>
              <DataSetInfo>
                <Icon icon="mdi:people" />
                <div style={{ marginLeft: "10px" }}>{dataSet.participant_count} users</div>
              </DataSetInfo>
            </>
          )
        }

        <DataSetData>
          <div>ID: {dataSet.id}</div>
          <div>PUB ID: {dataSet.public_id.substr(0, 24)}...</div>
          <div><b>{dataSet.document_count}</b> items</div>
          <div style={{ position: "absolute", right: "20px", top: "10px", fontSize: "12px", color: "#a0a6b1" }}>
            <DataSetOpenLink
              onClick={() => {
                if (activeUser?.user_type.match(/client/)) {
                  navigate(`/client/data-set/${dataSet.public_id}`);
                } else {
                  navigate(`/data-set/${dataSet.public_id}`);
                }
              }}
            >View <Icon icon="mdi:arrow-right" /></DataSetOpenLink>
          </div>
        </DataSetData>
        <DataSetToolBar dataSet={dataSet} />
      </DataSetCardWrapper>
    )
  }

  const DataSetsGrid = () => {
    console.log("!!! FILTERED data SETS: ", filteredDataSets);
    return (
      <DataSetsGridWrapper>
        { filteredDataSets.map(dataSet => {
          return <div key={dataSet.id} style={{ marginRight: "24px", marginBottom: "24px" }}><DataSetCard dataSet={dataSet} /></div>
        })
        }
      </DataSetsGridWrapper>
    );
  }

  return (
    <div
      style={{
        display: show ? "flex" : "none",
        flexDirection: "column",
        width: "100%",
      }}
    >
      <div style={{ fontSize: "32px", fontWeight: "bold" }}>
        <div style={{ color: "var(--main-font-color)" }}>Data Sets</div>
        <div style={{ fontSize: "16px", fontWeight: "italic" }}>
          ({dataSets.length > filteredDataSets.length ? `${filteredDataSets.length} of ${dataSets.length} Data Sets` : `${dataSets.length} Data Sets`})
        </div>
      </div>
      <div style={{ position: "absolute", right: "20px", top: "30px" }}>
        <Button
          onClick={() => {
            if (activeUser?.user_type.match(/client/)) {
              navigate(`/client/data-set/create`);
            } else {
              navigate(`/data-set/create`);
            }
          }}
        >+ Create Data Set</Button>
      </div>

      <div style={{ marginTop: "24px", width: "100%" }}>
        <SearchBar>
          <label style={{ marginRight: "10px", color: "var(--main-font-color)" }}>Sort by: </label>
          <div style={{ width: "200px", marginRight: "20px" }}>
            <FormSelector
              placeholder="Sort By"
              value={sortBy}
              options={sortByOptions}
              onChange={(e) => {
                setSortBy(e.target.value);
              }}
            />
          </div>
          <VerticalSeparator />

          <SmallButton className={display === "list" ? "selected" : ""} style={{ marginRight: "10px" }}
            onClick={() => { setDisplay("list") }}>
            <Icon icon="material-symbols:list" />
          </SmallButton>
          <SmallButton className={display === "grid" ? "selected" : ""} style={{ marginRight: "10px" }}
            onClick={() => { setDisplay("grid") }}>
            <Icon icon="material-symbols:grid-view-outline" />
          </SmallButton>
          <div style={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
            <SearchBox>
              <Icon icon="material-symbols:search" />
              <input value={searchTerm} type="text" onChange={(e) => {
                setSearchTerm(e.target.value);
              }} />

              {searchTerm &&
                <div style={{ cursor: "pointer" }} onClick={() => setSearchTerm("")}>
                  <Icon icon="material-symbols:x-circle" />
                </div>
              }
            </SearchBox>
          </div>
        </SearchBar>
      </div>

      {
        display === "grid" &&
        <div style={{ marginTop: "24px" }}>
          <DataSetsGrid />
        </div>
      }
      {
        display === "list" &&
        <DataSetsTable
          dataSets={filteredDataSets}
          onUpdated={(newDataSets) => {
            setDataSets(newDataSets);
          }}
        />
      }
      <QueryDialog dataSet={queryDataSet} show={showQueryDialog} onClose={() => setShowQueryDialog(false)} />
    </div>
  );
};

export default DataSets;
